import { apiFetch, getCurrentBrowserAuthHeaders } from '@/lib/api';

// Types
interface AccessLog {
  id: string;
  client_id: string;
  email: string;
  access_token?: string;
  ip_address?: string;
  user_agent?: string;
  accessed_at: string;
}

interface MagicLinkResult {
  success: boolean;
  message: string;
  data?: any;
}

type ClientRequestContext = {
  clientId?: string | null;
  email?: string | null;
};

// Vérifier ou créer le token permanent du client
export const ensureClientToken = async (clientId: string): Promise<{ success: boolean; token?: string; message: string }> => {
  try {
    const result = await apiFetch('/v1/auth/client/ensure-token', {
      method: 'POST',
      body: JSON.stringify({ client_id: clientId })
    });
    return result;
  } catch (error) {
    console.error('Erreur lors de la vérification du token:', error);
    return { success: false, message: 'Erreur de connexion' };
  }
};

// Valider un token permanent de client
// Retourne isNetworkError: true si l'échec est dû à un problème réseau (ne pas supprimer le token)
export const validateClientToken = async (
  token: string,
  context: ClientRequestContext = {},
): Promise<{ success: boolean; clientId?: string; message: string; isNetworkError?: boolean }> => {
  try {
    const result = await apiFetch('/v1/auth/client/validate-token', {
      method: 'POST',
      headers: getCurrentBrowserAuthHeaders({
        fallbackClientEmail: context.email,
        fallbackClientId: context.clientId,
      }),
      body: JSON.stringify({ token })
    });
    return { success: result.success, clientId: result.client_id, message: result.message };
  } catch (error) {
    console.error('Erreur lors de la validation du token:', error);
    // Marquer comme erreur réseau pour ne pas supprimer un token potentiellement valide
    return { success: false, message: 'Erreur de connexion', isNetworkError: true };
  }
};

// Mettre à jour l'activité du client (heartbeat)
export const updateClientActivity = async (token: string, email: string): Promise<{ success: boolean; message: string }> => {
  try {
    const result = await apiFetch('/v1/auth/client/update-activity', {
      method: 'POST',
      body: JSON.stringify({ token, email })
    });
    return result;
  } catch (error) {
    // Ne pas logger l'erreur car c'est un appel silencieux en arrière-plan
    return { success: false, message: 'Erreur de connexion' };
  }
};

// Envoyer un lien magique par email
export const sendMagicLink = async (email: string, clientId: string): Promise<MagicLinkResult> => {
  try {
    const result = await apiFetch('/v1/auth/client/send-magic-link', {
      method: 'POST',
      headers: getCurrentBrowserAuthHeaders({
        fallbackClientEmail: email,
        fallbackClientId: clientId,
      }),
      body: JSON.stringify({ email, client_id: clientId })
    });
    return result;
  } catch (error) {
    console.error('Erreur lors de l\'envoi du lien magique:', error);
    
    // Extraire le message d'erreur spécifique du serveur
    const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
    
    // Vérifier si c'est une erreur HTTP 403 (domaine non autorisé)
    if (errorMessage.includes('HTTP 403')) {
      return { 
        success: false, 
        message: 'Accès non autorisé. Votre adresse email n\'est pas autorisée pour ce client. Contactez votre administrateur ou ac@ailix.ai pour obtenir un accès.' 
      };
    }
    
    return { 
      success: false, 
      message: errorMessage.includes('HTTP') ? errorMessage : 'Erreur de connexion' 
    };
  }
};

// Valider un lien magique
export const validateMagicLink = async (magicToken: string, email: string, clientId: string): Promise<MagicLinkResult> => {
  try {
    const result = await apiFetch('/v1/auth/client/validate-magic-link', {
      method: 'POST',
      headers: getCurrentBrowserAuthHeaders({
        fallbackClientEmail: email,
        fallbackClientId: clientId,
      }),
      body: JSON.stringify({ magic_token: magicToken, email, client_id: clientId })
    });
    // Normaliser la forme des données
    const normalized: MagicLinkResult = {
      success: !!result.success,
      message: result.message,
      data: result.data
        ? {
            ...result.data,
            clientToken: result.data.clientToken || result.data.client_token || result.data.clienttoken,
          }
        : undefined,
    };
    return normalized;
  } catch (error) {
    console.error('Erreur lors de la validation du lien magique:', error);
    return { success: false, message: 'Erreur de connexion' };
  }
};

// Fonction logAccess supprimée - gérée côté backend

// Obtenir les logs d'accès d'un client (pour l'admin)
export const getClientAccessLogs = async (clientId: string): Promise<{ success: boolean; data?: AccessLog[]; message: string }> => {
  try {
    const result = await apiFetch(`/v1/auth/client/access-logs/${clientId}`);
    return result;
  } catch (error) {
    console.error('Erreur lors de la récupération des logs:', error);
    return { success: false, message: 'Erreur de connexion' };
  }
};

// Obtenir le token d'un client (pour l'admin)
export const getClientToken = async (clientId: string): Promise<{ success: boolean; token?: string; message: string }> => {
  try {
    const result = await apiFetch(`/v1/auth/client/token/${clientId}`);
    return result;
  } catch (error) {
    console.error('Erreur lors de la récupération du token:', error);
    return { success: false, message: 'Erreur de connexion' };
  }
};

// Révoquer le token d'un client (pour l'admin)
export const revokeClientToken = async (clientId: string): Promise<{ success: boolean; message: string }> => {
  try {
    const result = await apiFetch('/v1/auth/client/revoke-token', {
      method: 'POST',
      body: JSON.stringify({ client_id: clientId })
    });
    return result;
  } catch (error) {
    console.error('Erreur lors de la révocation du token:', error);
    return { success: false, message: 'Erreur de connexion' };
  }
};

// Régénérer le token d'un client (pour l'admin)
export const regenerateClientToken = async (clientId: string): Promise<{ success: boolean; token?: string; message: string }> => {
  try {
    const result = await apiFetch('/v1/auth/client/regenerate-token', {
      method: 'POST',
      body: JSON.stringify({ client_id: clientId })
    });
    return result;
  } catch (error) {
    console.error('Erreur lors de la régénération du token:', error);
    return { success: false, message: 'Erreur de connexion' };
  }
};
